// src/components/product/ProductImageGallery.jsx
import React, { useState } from 'react';
import useSnapScroller from '../../hooks/useSnapScroller';

/**
 * Product Image Gallery Component
 *
 * Mobile: swipeable strip using native CSS scroll snapping, with dot indicators.
 * Desktop (lg+): main image with clickable thumbnails.
 */
const ProductImageGallery = ({ images = [], alt = '' }) => {
  const imageUrls = images.length > 0 ? images : ['/placeholder-product.png'];
  const hasMultiple = imageUrls.length > 1;

  const [selectedImage, setSelectedImage] = useState(0);
  const { scrollerRef, activeSlide, handleScroll, scrollToSlide } =
    useSnapScroller(imageUrls.length);

  return (
    <div>
      {/* Mobile - swipeable */}
      <div className="lg:hidden">
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide rounded-lg"
        >
          {imageUrls.map((url, index) => (
            <div
              key={index}
              className="w-full flex-shrink-0 snap-center aspect-square bg-gray-100"
            >
              <img
                src={url}
                alt={`${alt} ${index + 1}`}
                className="w-full h-full object-cover"
                draggable={false}
              />
            </div>
          ))}
        </div>

        {hasMultiple && (
          <div className="flex justify-center gap-2 mt-4">
            {imageUrls.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToSlide(index)}
                aria-label={`Go to image ${index + 1}`}
                className={`
                  h-2 rounded-full transition-all
                  ${activeSlide === index ? 'w-6 bg-blue-600' : 'w-2 bg-gray-300'}
                `}
              />
            ))}
          </div>
        )}
      </div>

      {/* Desktop - main image + thumbnails */}
      <div className="hidden lg:block">
        <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-4">
          <img
            src={imageUrls[selectedImage]}
            alt={alt}
            className="w-full h-full object-cover"
          />
        </div>

        {hasMultiple && (
          <div className="grid grid-cols-4 gap-2">
            {imageUrls.map((url, index) => (
              <button
                key={index}
                onClick={() => setSelectedImage(index)}
                className={`
                  aspect-square rounded-lg overflow-hidden border-2 transition-all
                  ${selectedImage === index
                    ? 'border-blue-600'
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
              >
                <img
                  src={url}
                  alt={`${alt} ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductImageGallery;
